<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'

import { useUtils } from '@/composables/useUtils'
import { useBroadcastStore } from '@/stores/broadcast'
import { storeToRefs } from 'pinia'
import PhotoLoader from '@/components/elements/PhotoLoader.vue'
import AppLink from '@/components/layout/AppLink.vue'

const { getStartTime } = useUtils()

const settings = {
  itemsToShow: 1,
  snapAlign: 'center'
}
const breakpoints = {
  700: {
    itemsToShow: 2,
    snapAlign: 'start'
  },
  1024: {
    itemsToShow: 3,
    snapAlign: 'start'
  },
  1330: {
    itemsToShow: 4,
    snapAlign: 'start'
  }
}

const broadcast = useBroadcastStore()
const { programs, programItems } = storeToRefs(broadcast)
const { fetchPrograms } = broadcast
import { useRoute } from 'vue-router'
const loaderBroadcast = ref(true)

const showBroadcast = computed(() => !route.path.includes('program-tv'))

const showMore = computed(() => programs.value.show_more)

const programsItems = computed(() => programItems.value.length)

const activeElement = ref(null)
const addActiveElem = (index) => {
  activeElement.value = index
}

const route = useRoute()
const searchQuery = computed(() => route.query.query)

watch(
  () => searchQuery.value,
  () => {
    addActiveElem()
  }
)

onMounted(() => {
  fetchPrograms().finally(() => {
    loaderBroadcast.value = false
  })
  addActiveElem()
})
</script>
<template>
  <div class="broadcast-slider" id="broadcast" v-if="showBroadcast && programsItems">
    <template v-if="loaderBroadcast">
      <Skeletor class="" width="100%" height="97" style="margin: 0 8px" />
    </template>
    <template v-else>
      <div v-if="programs" class="broadcast-slider__label broadcast-label">
        <p class="broadcast-label__title">{{ programs.title }}</p>
        <app-link
          class="broadcast-label__link"
          :to="showMore.url"
          :routing-data="showMore.routing_data"
          :path-type="showMore.type"
        >
          {{ showMore.title }}
          <svg width="10" height="10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="m5.303.547-.787.787 3.114 3.12H.839V5.57H7.63L4.516 8.689l.787.787 4.464-4.464L5.303.547z"
              fill="#B91D21"
            />
          </svg>
        </app-link>
      </div>
      <div class="broadcast-slider__wrapper">
        <Carousel :settings="settings" :breakpoints="breakpoints">
          <slide
            v-for="(slide, index) in programItems"
            class="broadcast-slider__items"
            :class="{ 'slider-loaded': programs, 'active-element': index === activeElement }"
            :key="index"
            @click="addActiveElem(index)"
          >
            <app-link
              class="broadcast-slider__item"
              :to="slide.url"
              :routing-data="slide.routing_data"
              :path-type="slide.type"
            >
              <photo-loader
                img-class="broadcast-img"
                :image="slide?.program?.image"
                width-skeletor="167"
                height-skeletor="97"
                :imgWidth="170"
                :imgHeight="100"
              />
              <div class="broadcast-slider__desc">
                <div
                  class="broadcast-slider__time"
                  :class="{ 'broadcast-slider__time--current': index === 0 }"
                >
                  <span v-text="getStartTime(slide)"></span>
                </div>
                <div class="broadcast-slider__name"><span v-text="slide.title"></span></div>
                <div class="broadcast-slider__category">
                  <span
                    v-text="slide.program && slide.program.type && slide.program.type.title"
                  ></span>
                </div>
              </div>
            </app-link>
          </slide>
          <template #addons>
            <navigation />
          </template>
        </Carousel>
      </div>
    </template>
  </div>
</template>

<style lang="scss">
@import '@/assets/scss/components/layout/navigation-broadcast.scss';
</style>
